import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaUsers, FaShieldAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import roleService from '../../services/roleService';
import './RoleManagement.css';

const RoleManagement = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await roleService.getAllRoles();
      setRoles(response.data || []);
    } catch (error) {
      setError('Failed to fetch roles');
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = () => {
    navigate('/app/settings/roles/create');
  };

  const handleEditRole = (role) => {
    navigate(`/app/settings/roles/${role.id}/edit`);
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`Are you sure you want to delete the role "${role.name}"?`)) {
      return;
    }

    try {
      await roleService.deleteRole(role.id);
      setSuccess(`Role "${role.name}" deleted successfully`);
      fetchRoles();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to delete role';
      setError(errorMsg);
      setTimeout(() => setError(''), 5000);
    }
  };

  const getRoleUserCount = (role) => {
    return role.users ? role.users.length : 0;
  };

  const getPermissionCount = (role) => {
    if (!role.permissions) return 0;
    return Object.values(role.permissions).filter(val => val === true).length;
  };

  if (loading) {
    return (
      <div className="role-management-container">
        <div className="loading-spinner">Loading roles...</div>
      </div>
    );
  }

  return (
    <div className="role-management-container">
      <div className="role-management-header">
        <div>
          <h1><FaShieldAlt /> Role Management</h1>
          <p>Manage user roles and permissions for your organization</p>
        </div>
        <button className="btn-primary" onClick={handleCreateRole}>
          <FaPlus /> Create Role
        </button>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="alert alert-error"
        >
          {error}
        </motion.div>
      )}

      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="alert alert-success"
        >
          {success}
        </motion.div>
      )}

      <div className="roles-grid">
        {roles.map((role) => (
          <motion.div
            key={role.id}
            className="role-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="role-card-header">
              <h3>{role.name}</h3>
              <div className="role-actions">
                <button
                  className="btn-icon"
                  onClick={() => handleEditRole(role)}
                  title="Edit Role"
                >
                  <FaEdit />
                </button>
                <button
                  className="btn-icon btn-danger"
                  onClick={() => handleDeleteRole(role)}
                  title="Delete Role"
                >
                  <FaTrash />
                </button>
              </div>
            </div>

            <p className="role-description">{role.description || 'No description'}</p>

            <div className="role-stats">
              <div className="role-stat">
                <FaUsers />
                <span>{getRoleUserCount(role)} Users</span>
              </div>
              <div className="role-stat">
                <FaShieldAlt />
                <span>{getPermissionCount(role)} Permissions</span>
              </div>
            </div>

            {role.permissions && Object.keys(role.permissions).length > 0 && (
              <div className="role-permissions-preview">
                <h4>Permissions:</h4>
                <div className="permissions-tags">
                  {Object.entries(role.permissions)
                    .filter(([_, value]) => value === true)
                    .slice(0, 5)
                    .map(([key]) => (
                      <span key={key} className="permission-tag">
                        {key}
                      </span>
                    ))}
                  {getPermissionCount(role) > 5 && (
                    <span className="permission-tag more">
                      +{getPermissionCount(role) - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default RoleManagement;

// ---
